import * as React from 'react';
import { Intent, Button, H4, H3, Dialog, Classes, Tabs, Tab, H5, Tag, ProgressBar } from '@blueprintjs/core';

interface Props {
    isOpen: boolean;
    isLoading: boolean;
    analysisResults: any;
    onKeepPlan: () => void;
    onChangePlan: () => void;
    onUseCoachPlan: () => void;
}

interface State {
    progress: number;
}

export class PlanAnalysisModal extends React.Component<Props, State> {
    private progressInterval: NodeJS.Timeout | null = null;

    state: State = {
        progress: 0
    };

    componentDidUpdate(prevProps: Props) {
        // Start progress animation when loading begins
        if (!prevProps.isLoading && this.props.isLoading) {
            this.startProgressAnimation();
        }
        
        // Reset progress when loading stops
        if (prevProps.isLoading && !this.props.isLoading) {
            this.stopProgressAnimation();
        }
    }

    componentWillUnmount() {
        this.stopProgressAnimation();
    }

    startProgressAnimation = () => {
        this.setState({ progress: 0 });
        
        // Animate progress over 10 seconds (100ms intervals = 100 steps)
        this.progressInterval = setInterval(() => {
            this.setState(prevState => {
                const newProgress = Math.min(prevState.progress + 1, 100);
                return { progress: newProgress };
            });
        }, 100); // Update every 100ms for smooth animation
    }

    stopProgressAnimation = () => {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
        this.setState({ progress: 0 });
    }

    renderLoadingState = () => {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <H4 style={{ marginBottom: '30px' }}>Analyzing your plan...</H4>
                <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                    <ProgressBar 
                        value={this.state.progress / 100} 
                        intent={Intent.PRIMARY}
                        animate={true}
                        stripes={true}
                    />
                    <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#666' }}>
                        {Math.round(this.state.progress)}% complete
                    </p>
                </div>
            </div>
        );
    }

    renderVariation1_PriorityMessage = () => {
        const { analysisResults } = this.props;
        const assessment = analysisResults?.assessment || {};
        const priorityMessage = analysisResults?.priority_message || {};
        const betterPlan = analysisResults?.better_plan;
        
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <H3 style={{ margin: 0, marginRight: '15px' }}>Your Plan Assessment</H3>
                    <Tag intent={analysisResults?.student_grade >= 0.7 ? Intent.SUCCESS : analysisResults?.student_grade >= 0.4 ? Intent.WARNING : Intent.DANGER}>
                        {Math.round((analysisResults?.student_grade || 0) * 100)}%
                    </Tag>
                </div>

                {betterPlan && (
                    <div style={{ 
                        backgroundColor: '#e8f5e8', 
                        padding: '20px', 
                        borderRadius: '8px', 
                        borderLeft: '5px solid #059669',
                        marginBottom: '25px',
                        border: '1px solid #d1fae5'
                    }}>
                        <H4 style={{ marginTop: 0, color: '#059669', display: 'flex', alignItems: 'center' }}>
                            ✨ Improved Plan Suggestion
                        </H4>
                        <p style={{ fontSize: '1.2rem', lineHeight: '1.7', margin: 0, fontWeight: '500' }}>
                            {betterPlan}
                        </p>
                    </div>
                )}

                <div style={{ 
                    backgroundColor: '#f5f8fa', 
                    padding: '20px', 
                    borderRadius: '6px', 
                    borderLeft: '4px solid #137cbd',
                    marginBottom: '20px'
                }}>
                    <H4 style={{ marginTop: 0, color: '#137cbd' }}>Priority Focus</H4>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', margin: 0 }}>
                        {priorityMessage.content}
                    </p>
                </div>

                <div style={{ marginTop: '20px' }}>
                    <H5>Readiness Level</H5>
                    <p style={{ fontSize: '1rem' }}>{assessment.readiness_to_execute}</p>
                </div>
            </div>
        );
    }

    renderVariation2_CategorizedFeedback = () => {
        const { analysisResults } = this.props;
        const betterPlan = analysisResults?.better_plan;
        
        return (
            <div>
                <H4>Leadership Analysis</H4>
                
                {betterPlan && (
                    <div style={{ 
                        backgroundColor: '#f0fdf4', 
                        padding: '15px', 
                        borderRadius: '6px', 
                        borderLeft: '4px solid #059669',
                        marginBottom: '20px'
                    }}>
                        <H5 style={{ marginTop: 0, color: '#059669' }}>💡 Enhanced Plan Approach</H5>
                        <p style={{ fontSize: '1rem', margin: 0 }}>{betterPlan}</p>
                    </div>
                )}
                
                <div style={{ marginBottom: '20px' }}>
                    <H5>🎯 Objective Gaps</H5>
                    {analysisResults?.objective_gaps?.map((gap: any, index: number) => (
                        <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fdf2f2', borderRadius: '4px' }}>
                            <strong>{gap.objective}</strong>
                            <p style={{ margin: '5px 0', fontSize: '0.95rem' }}>{gap.what_plan_needs}</p>
                            <small style={{ color: '#dc3545' }}>⚠️ {gap.warning_signals}</small>
                        </div>
                    ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <H5>⚡ Immediate Actions</H5>
                    {analysisResults?.immediate_actions?.map((action: any, index: number) => (
                        <div key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '4px' }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{action.action}</p>
                            {action.talking_point && (
                                <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.9rem' }}>
                                    💬 "{action.talking_point}"
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                <div>
                    <H5>🧠 Key Insights</H5>
                    {analysisResults?.insights?.map((insight: any, index: number) => (
                        <div key={index} style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fffbf0', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <strong style={{ textTransform: 'capitalize', marginRight: '8px' }}>
                                    {insight.type?.replace('_', ' ')}:
                                </strong>
                                <Tag intent={
                                    insight.type === 'strength' ? Intent.SUCCESS : 
                                    insight.type === 'growth_area' ? Intent.PRIMARY : 
                                    Intent.WARNING
                                } minimal>
                                    {insight.type?.replace('_', ' ')}
                                </Tag>
                            </div>
                            <p style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>{insight.description}</p>
                            <p style={{ margin: 0, fontSize: '0.9rem', fontStyle: 'italic', color: '#666' }}>
                                <strong>Development focus:</strong> {insight.development_area}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderVariation3_ObjectiveGapAnalysis = () => {
        const { analysisResults } = this.props;
        
        return (
            <div>
                <H4>Objective Gap Analysis</H4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f5f8fa' }}>
                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e1e8ed' }}>Objective</th>
                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e1e8ed' }}>Status</th>
                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e1e8ed' }}>What's Needed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {analysisResults?.objective_gaps?.map((gap: any, index: number) => (
                            <tr key={index}>
                                <td style={{ padding: '10px', border: '1px solid #e1e8ed', fontWeight: 'bold' }}>
                                    {gap.objective}
                                </td>
                                <td style={{ padding: '10px', border: '1px solid #e1e8ed' }}>
                                    <Tag intent={Intent.DANGER}>Missing</Tag>
                                </td>
                                <td style={{ padding: '10px', border: '1px solid #e1e8ed', fontSize: '0.9rem' }}>
                                    {gap.what_plan_needs}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ marginTop: '20px' }}>
                    <H5>Risk Assessment</H5>
                    {analysisResults?.risks?.map((risk: any, index: number) => (
                        <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fef2f2', borderRadius: '4px', borderLeft: '4px solid #dc2626' }}>
                            <strong>Risk: </strong>{risk.risk}
                            <br />
                            <strong>Mitigation: </strong>{risk.mitigation}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderVariation4_AlternativePlan = () => {
        const { analysisResults } = this.props;
        const betterPlan = analysisResults?.better_plan;
        
        return (
            <div>
                <H4>Plan Enhancement Opportunities</H4>
                
                {betterPlan && (
                    <div style={{ 
                        backgroundColor: '#f0f9ff', 
                        padding: '20px', 
                        borderRadius: '8px', 
                        border: '2px solid #0ea5e9',
                        marginBottom: '25px'
                    }}>
                        <H5 style={{ marginTop: 0, color: '#0ea5e9', display: 'flex', alignItems: 'center' }}>
                            🔄 Alternative Plan Approach
                        </H5>
                        <p style={{ fontSize: '1.1rem', lineHeight: '1.6', margin: 0 }}>
                            {betterPlan}
                        </p>
                    </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>
                    <div>
                        <H5 style={{ color: '#dc2626' }}>Current Plan Issues</H5>
                        <div style={{ padding: '15px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                            <p><strong>Primary Weakness:</strong> {analysisResults?.overall_assessment?.primary_weakness}</p>
                            <p><strong>Readiness:</strong> {analysisResults?.assessment?.readiness_to_execute}</p>
                            <div>
                                <strong>Key Blindspots:</strong>
                                <ul>
                                    {analysisResults?.overall_assessment?.key_blindspots?.map((blindspot: string, index: number) => (
                                        <li key={index}>{blindspot}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <H5 style={{ color: '#059669' }}>Recommended Improvements</H5>
                        <div style={{ padding: '15px', backgroundColor: '#f0fdf4', borderRadius: '6px' }}>
                            {analysisResults?.opportunities?.map((opportunity: any, index: number) => (
                                <div key={index} style={{ marginBottom: '15px' }}>
                                    <p><strong>Opportunity:</strong> {opportunity.opportunity}</p>
                                    <p><strong>Action:</strong> {opportunity.action}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                    <H5>Strategic Development Resources</H5>
                    {analysisResults?.strategic_recommendations?.map((rec: any, index: number) => (
                        <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                            <p style={{ margin: '0 0 5px 0' }}><strong>{rec.recommendation}</strong></p>
                            <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem' }}>📚 {rec.resource}</p>
                            <small>⏱️ {rec.time_investment}</small>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderVariation5_TieredCoaching = () => {
        const { analysisResults } = this.props;
        const betterPlan = analysisResults?.better_plan;
        
        return (
            <div>
                {betterPlan && (
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <H4 style={{ margin: 0, marginRight: '10px' }}>🎯 Recommended Plan Direction</H4>
                            <Tag intent={Intent.SUCCESS}>Suggested Approach</Tag>
                        </div>
                        <div style={{ 
                            backgroundColor: '#ecfdf5', 
                            padding: '20px', 
                            borderRadius: '8px', 
                            borderLeft: '5px solid #10b981'
                        }}>
                            <p style={{ fontSize: '1.1rem', lineHeight: '1.6', margin: 0 }}>
                                {betterPlan}
                            </p>
                        </div>
                    </div>
                )}
                
                <div style={{ marginBottom: '25px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <H4 style={{ margin: 0, marginRight: '10px' }}>🚀 Fix This First</H4>
                        <Tag intent={Intent.DANGER}>High Priority</Tag>
                    </div>
                    
                    <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#fef2f2', 
                        borderRadius: '6px', 
                        borderLeft: '4px solid #dc2626',
                        marginBottom: '15px'
                    }}>
                        <p style={{ fontSize: '1.1rem', margin: 0 }}>
                            {analysisResults?.priority_message?.content}
                        </p>
                    </div>

                    <H5>Immediate Actions (This Week)</H5>
                    {analysisResults?.immediate_actions?.slice(0, 3).map((action: any, index: number) => (
                        <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            marginBottom: '10px',
                            padding: '10px',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <span style={{ 
                                marginRight: '10px', 
                                backgroundColor: '#dc2626', 
                                color: 'white', 
                                borderRadius: '50%', 
                                width: '24px', 
                                height: '24px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                            }}>
                                {index + 1}
                            </span>
                            <span>{action.action || action}</span>
                        </div>
                    ))}
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <H4 style={{ margin: 0, marginRight: '10px' }}>📈 Consider for Next Iteration</H4>
                        <Tag intent={Intent.PRIMARY}>Strategic Development</Tag>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <H5>Growth Opportunities</H5>
                        {analysisResults?.opportunities?.map((opp: any, index: number) => (
                            <div key={index} style={{ 
                                marginBottom: '10px',
                                padding: '10px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '4px',
                                borderLeft: '3px solid #0ea5e9'
                            }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{opp.opportunity}</p>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>{opp.action}</p>
                            </div>
                        ))}
                    </div>

                    <div>
                        <H5>Long-term Development</H5>
                        {analysisResults?.strategic_recommendations?.map((rec: any, index: number) => (
                            <div key={index} style={{ 
                                marginBottom: '10px',
                                padding: '10px',
                                backgroundColor: '#fefce8',
                                borderRadius: '4px'
                            }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem' }}>{rec.recommendation}</p>
                                <small style={{ color: '#65a30d' }}>⏱️ {rec.time_investment}</small>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderAnalysisResults = () => {
        const { analysisResults } = this.props;
        
        if (!analysisResults) {
            return null;
        }

        return (
            <Tabs id="analysis-variations" defaultSelectedTabId="variation1">
                <Tab id="variation1" title="Variation 1" panel={this.renderVariation1_PriorityMessage()} />
                <Tab id="variation2" title="Variation 2" panel={this.renderVariation2_CategorizedFeedback()} />
                <Tab id="variation3" title="Variation 3" panel={this.renderVariation3_ObjectiveGapAnalysis()} />
                <Tab id="variation4" title="Variation 4" panel={this.renderVariation4_AlternativePlan()} />
                <Tab id="variation5" title="Variation 5" panel={this.renderVariation5_TieredCoaching()} />
            </Tabs>
        );
    }

    renderActionButtons = () => {
        if (this.props.isLoading || !this.props.analysisResults) {
            return null;
        }

        const betterPlan = this.props.analysisResults?.better_plan;

        return (
            <div className={Classes.DIALOG_FOOTER}>
                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                <Button 
                        intent={Intent.NONE}
                        large
                        onClick={this.props.onKeepPlan}
                        style={{ flex: 1 }}
                    >
                        Execute My Plan
                    </Button>
                    {betterPlan && (
                        <Button 
                            intent={Intent.PRIMARY}
                            large
                            onClick={this.props.onUseCoachPlan}
                            style={{ flex: 1 }}
                        >
                            Use Coach's Plan
                        </Button>
                    )}
                    <Button 
                        intent={Intent.SUCCESS}
                        large
                        onClick={this.props.onChangePlan}
                        style={{ flex: 1 }}
                    >
                        Change My Plan
                    </Button>


                </div>
            </div>
        );
    }

    render() {
        return (
            <Dialog
                isOpen={this.props.isOpen}
                onClose={() => {}}
                title="Plan Analysis"
                canEscapeKeyClose={false}
                canOutsideClickClose={false}
                style={{ width: '900px', maxWidth: '90vw' }}
            >
                <div className={Classes.DIALOG_BODY}>
                    {this.props.isLoading ? this.renderLoadingState() : this.renderAnalysisResults()}
                </div>
                
                {this.renderActionButtons()}
            </Dialog>
        );
    }
}
